import { morph, MORPH_OPTIONS } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #eventTarget = null;
  #selectionVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#selectionVersion++;
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
    super.disconnectedCallback();
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!this.isConnected || !(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (event.target.closest('.shopify-section, dialog') !== this.#eventTarget) return;
    const selectionVersion = ++this.#selectionVersion;

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || selectionVersion !== this.#selectionVersion || !detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newProductPrice = html.querySelector(`product-price[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`);
        if (!newProductPrice) return;

        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        let priceContainer = this.querySelector('[ref="priceContainer"]');
        if (newPrice) {
          const priceCopy = newPrice.cloneNode(true);
          if (priceContainer) {
            morph(priceContainer, priceCopy, { ...MORPH_OPTIONS, childrenOnly: false });
          } else {
            this.prepend(priceCopy);
            priceContainer = this.querySelector('[ref="priceContainer"]');
          }
        }

        const volumePricingNote = this.querySelector('[ref="volumePricingNote"]');
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else {
          const noteCopy = newNote.cloneNode(true);
          if (volumePricingNote) {
            volumePricingNote.replaceWith(noteCopy);
          } else {
            priceContainer?.after(noteCopy);
          }
        }
        this.updatedCallback();

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
